import { useCallback, useRef, useState } from "react";
import { useValidateAbTestOutput } from "@/resources/ab-test/ab-test.api";
import type { AiValidationResult } from "@/types/ai-validation";

interface UseRevalidateVariantBParams {
  parsedProductId: string;
  selectedBrandId: number | null;
  selectedFields: string[];
  variantBTitle: string;
  variantBBullets: Record<string, string>;
  setCopyValidation: (v: AiValidationResult | null) => void;
}

/**
 * AUT-396 (dev-scope, in-brief): re-run the (still non-blocking) Variant B validation when the user
 * EDITS the copy, so a manual change is validated and surfaced in the ValidationPanel *before* they
 * click Create. Previously validation only ran on generation and again at submit -- but the submit
 * verdict was written and instantly hidden by the success modal + redirect, so an edit (e.g. adding
 * an emoji) was never actually shown. This keeps the non-blocking behaviour; it only makes the
 * verdict visible while there is still something the user can do about it.
 *
 * Returns `revalidate` (call it on field blur-with-change) and `isValidating` for a pending hint.
 * Never blocks and never surfaces an error -- a failed check just leaves the previous verdict.
 */
export function useRevalidateVariantB({
  parsedProductId,
  selectedBrandId,
  selectedFields,
  variantBTitle,
  variantBBullets,
  setCopyValidation,
}: UseRevalidateVariantBParams) {
  const validateOutput = useValidateAbTestOutput();
  const [isValidating, setIsValidating] = useState(false);
  // Last content (+ brand/product) actually validated, to skip a redundant Gemini round-trip.
  const lastValidatedRef = useRef<string | null>(null);
  // Monotonic request id, so a slower earlier call can't overwrite a newer verdict.
  const requestSeqRef = useRef(0);

  // `override` carries the exact just-edited field/value from the blur event, so validation never
  // races the React state update behind `variantBTitle`/`variantBBullets`.
  const revalidate = useCallback(async (override?: { field: string; value: string }) => {
    const productId = Number(parsedProductId);
    if (!selectedBrandId || !Number.isFinite(productId) || productId <= 0) return;

    const bulletFields = selectedFields.filter((f) => f.startsWith("bullet-"));
    const titleValue = override?.field === "title" ? override.value : variantBTitle;
    const bulletValue = (f: string) => (override?.field === f ? override.value : (variantBBullets[f] ?? ""));
    const title = selectedFields.includes("title") ? titleValue.trim() : undefined;
    const bulletsByField = Object.fromEntries(bulletFields.map((f) => [f, bulletValue(f).trim()]));

    const hasContent = (title?.length ?? 0) > 0 || Object.values(bulletsByField).some((b) => b.length > 0);
    if (!hasContent) return;

    const output = JSON.stringify({ title, bulletsByField });
    // Skip a redundant Gemini call when the exact same content was already validated (a field
    // blurred without a real change, or an edit reverted). Keyed on brand/product too so a context
    // switch still re-validates. Only set on success below, so a failed check stays retryable.
    const dedupeKey = `${productId}:${selectedBrandId}:${output}`;
    if (dedupeKey === lastValidatedRef.current) return;

    const seq = ++requestSeqRef.current;
    setIsValidating(true);
    try {
      const result = await validateOutput.mutateAsync({
        productId,
        brandId: selectedBrandId,
        output,
        featureKey: "ab-test-variant-b-generator",
      });
      if (seq !== requestSeqRef.current) return; // a newer edit superseded this; drop the stale verdict
      lastValidatedRef.current = dedupeKey;
      setCopyValidation(result);
    } catch {
      // Non-blocking: keep the previous verdict; never surface an error toast.
    } finally {
      if (seq === requestSeqRef.current) setIsValidating(false);
    }
  }, [parsedProductId, selectedBrandId, selectedFields, variantBTitle, variantBBullets, validateOutput, setCopyValidation]);

  return { revalidate, isValidating };
}
